<template>
  <div class="min-h-screen bg-[#fafbfc] px-4 py-10 font-['Poppins'] sm:px-6 lg:px-8">
    <div class="mx-auto w-full max-w-md rounded-xl border border-zinc-200 bg-white p-6 shadow-sm sm:p-8">
      <img
        class="mx-auto h-8 w-auto"
        src="@/assets/media/images/platforma-logo-in-color.svg"
        alt="Platforma Logo"
      >
      <h1 class="mt-6 text-center text-2xl font-semibold text-zinc-900">
        Reset Password
      </h1>
      <p class="mt-2 text-center text-sm text-zinc-500">
        Enter your creator account email and we will send a reset link.
      </p>

      <form
        class="mt-6 space-y-4"
        @submit.prevent="requestPasswordReset"
      >
        <div>
          <label
            for="recover-email"
            class="block text-sm font-medium text-zinc-700"
          >Email address</label>
          <input
            id="recover-email"
            v-model="email"
            type="email"
            autocomplete="email"
            required
            class="mt-1 block w-full rounded-md border-0 py-2 text-zinc-900 shadow-sm ring-1 ring-inset ring-zinc-300 placeholder:text-zinc-400 focus:ring-2 focus:ring-inset focus:ring-[#2D8CFF] sm:text-sm"
            placeholder="you@example.com"
            :disabled="isSubmitting"
          >
        </div>

        <button
          type="submit"
          class="inline-flex w-full items-center justify-center rounded-md bg-[#2D8CFF] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#2577d9] disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="isSubmitting || !email"
        >
          {{ isSubmitting ? 'Sending...' : 'Send Reset Link' }}
        </button>
      </form>

      <p
        v-if="successMessage"
        class="mt-4 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700"
      >
        {{ successMessage }}
      </p>
      <p
        v-if="errorMessage"
        class="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600"
      >
        {{ errorMessage }}
      </p>

      <div class="mt-6 text-center">
        <RouterLink
          to="/"
          class="text-sm font-medium text-[#2D8CFF] hover:underline"
        >
          Back to login
        </RouterLink>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';

const isDev = process.env.NODE_ENV === 'development';
const baseUrl = isDev
  ? process.env.VUE_APP_API_PROXY_URL
  : '';

const email = ref('');
const isSubmitting = ref(false);
const successMessage = ref('');
const errorMessage = ref('');

const requestPasswordReset = async () => {
  successMessage.value = '';
  errorMessage.value = '';
  const normalizedEmail = String(email.value || '').trim().toLowerCase();
  if (!normalizedEmail) {
    errorMessage.value = 'Please provide an email address.';
    return;
  }

  isSubmitting.value = true;
  try {
    const response = await fetch(`${baseUrl}/api/auth/password-recovery/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email: normalizedEmail }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload?.success === false) {
      throw new Error(String(payload?.message || payload?.error || 'Failed to request password reset.'));
    }
    successMessage.value = String(payload?.message || 'If an account exists for this email, a reset link will be sent.');
  } catch (error) {
    console.error('Error requesting password reset:', error);
    errorMessage.value = String(error?.message || 'Unable to request password reset right now.');
  } finally {
    isSubmitting.value = false;
  }
};
</script>
